<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { startOfDay, subDays, getUnixTime } from 'date-fns';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ReportsAPI from 'dashboard/api/reports';
import copy from './en.json';

const { t, locale } = useI18n({
  useScope: 'local',
  messages: copy.REPORT_LOCALES,
  fallbackLocale: 'en',
});
const { accountId } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const featureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const canReadReports = computed(
  () =>
    featureEnabled.value(accountId.value, FEATURE_FLAGS.REPORTS) &&
    hasPermissions(
      ['administrator', 'report_manage'],
      getUserPermissions(currentUser.value, accountId.value)
    )
);
const REPORT_DAYS = 7;
const summary = ref(null);
const loading = ref(false);
const failed = ref(false);

watch(
  [canReadReports, accountId],
  async ([allowed], _previous, onCleanup) => {
    let active = true;
    onCleanup(() => {
      active = false;
    });
    summary.value = null;
    failed.value = false;
    loading.value = false;
    if (!allowed) return;
    loading.value = true;
    const until = new Date();
    const since = startOfDay(subDays(until, REPORT_DAYS - 1));
    try {
      const { data } = await ReportsAPI.getSummary(
        getUnixTime(since),
        getUnixTime(until),
        'account'
      );
      if (!active) return;
      summary.value = data;
    } catch {
      if (active) failed.value = true;
    } finally {
      if (active) loading.value = false;
    }
  },
  { immediate: true }
);

const count = value =>
  value === null || value === undefined
    ? t('UNAVAILABLE')
    : new Intl.NumberFormat(locale.value).format(value);
const metrics = computed(() => {
  if (!summary.value) return [];
  const s = summary.value;
  return [
    { label: t('COUNT'), value: count(s.conversations_count) },
    { label: t('INCOMING'), value: count(s.incoming_messages_count) },
    { label: t('OUTGOING'), value: count(s.outgoing_messages_count) },
    { label: t('RESOLUTIONS'), value: count(s.resolutions_count) },
  ];
});
</script>

<template>
  <section
    v-if="canReadReports"
    class="shrink-0 border-b border-n-weak bg-n-background p-3"
    :aria-label="t('TITLE')"
    :aria-busy="loading"
  >
    <p v-if="loading" role="status" class="py-3 text-sm text-n-slate-11">
      {{ t('LOADING') }}
    </p>
    <p v-else-if="failed" role="alert" class="py-3 text-sm text-n-ruby-11">
      {{ t('ERROR') }}
    </p>
    <dl
      v-else-if="summary"
      class="grid max-h-[45vh] grid-cols-2 gap-3 overflow-y-auto p-1 lg:grid-cols-4"
    >
      <div
        v-for="metric in metrics"
        :key="metric.label"
        class="relative flex min-w-0 flex-col items-center justify-center overflow-hidden rounded-xl border border-n-brand/20 bg-gradient-to-br from-n-blue-3 via-n-surface-1 to-n-blue-2 px-3 py-3 text-center shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-n-brand/50 hover:shadow-md hover:shadow-n-brand/10 motion-reduce:transition-none"
      >
        <span
          aria-hidden="true"
          class="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-n-brand to-transparent"
        />
        <dt class="relative text-xs leading-5 text-n-slate-11">
          {{ metric.label }}
        </dt>
        <dd
          class="relative mt-1 text-2xl font-semibold tabular-nums text-n-blue-11"
        >
          {{ metric.value }}
        </dd>
      </div>
    </dl>
  </section>
</template>
